import type { ReactiveController, ReactiveControllerHost } from 'lit'
import { asapScheduler, filter, fromEvent, map, merge, observeOn, Subject, take, takeUntil, timer } from 'rxjs'
import { deepActiveElement, firstFocusableDescendant, focusableDescendants } from './focus.js'

/**
 * How many siblings either side of each ancestor are kept as anchors. A `when()`
 * branch removes its nodes as one run, so the anchor that survives it is the
 * first sibling past that run — the part's marker comment at the latest.
 */
const ANCHOR_REACH = 16

/** One step of the path from the control up to its area, and the siblings around it. */
interface Rung {
	node: Node
	parent: Node | null
	/** Nearest first. */
	before: Node[]
	/** Nearest first. */
	after: Node[]
}

/**
 * Where a focused control stood, read while it still stood there. Pointer reads
 * along one path — O(depth × ANCHOR_REACH) — because this is taken on every
 * focus and click of every control; the walk of the area's tab stops runs once,
 * at hand-off. That walk measured 10.9 ms on a 12,917-element page.
 */
interface FocusRecord {
	/** The area focus may never leave. */
	scope: HTMLElement
	/** The tree scopes from the control's own outward, ending at the one holding `scope`. */
	trees: Array<Document | ShadowRoot>
	/** From the control outward, stopping below `scope`. */
	rungs: Rung[]
}

const shadowIncludingParent = (node: Node): Node | null =>
	node instanceof ShadowRoot ? node.host : node.parentNode

function shadowIncludingContains(ancestor: Node, node: Node | null): boolean {
	for (let at: Node | null = node; at; at = shadowIncludingParent(at)) {
		if (at === ancestor) return true
	}
	return false
}

function siblings(node: Node, step: (at: Node) => Node | null): Node[] {
	const found: Node[] = []
	for (let at = step(node); at && found.length < ANCHOR_REACH; at = step(at)) found.push(at)
	return found
}

/**
 * `node` and its flat-tree ancestors, nearest first. A host's light child climbs
 * to the slot it is assigned to and a shadow root's child to the host; the chain
 * stops short of the document at a light child no slot takes, which renders nowhere.
 */
function renderedChain(node: Node): Node[] {
	const chain: Node[] = []
	for (let at: Node | null = node; at; ) {
		chain.push(at)
		const parent: Node | null = at.parentNode
		at =
			parent instanceof ShadowRoot
				? parent.host
				: parent instanceof Element && parent.shadowRoot
					? at instanceof Element || at instanceof Text
						? at.assignedSlot
						: null
					: parent
	}
	return chain
}

/** Whether the flat tree renders `node` in the document. */
const rendered = (node: Node): boolean => renderedChain(node).at(-1) instanceof Document

/**
 * Negative when `a` comes before `b` in the order `focusableDescendants` walks —
 * the flat tree, where slotted nodes stand at their slot — positive after, 0 for
 * one node. A node is after each of its flat-tree ancestors.
 */
function compareInWalkOrder(a: Node, b: Node): number {
	if (a === b) return 0
	const chainOfA = renderedChain(a)
	const chainOfB = renderedChain(b)
	const common = chainOfA.find(node => chainOfB.includes(node))
	if (!common) return 1
	if (common === a) return -1
	if (common === b) return 1
	const branchOfA = chainOfA[chainOfA.indexOf(common) - 1]
	const branchOfB = chainOfB[chainOfB.indexOf(common) - 1]
	// A slot renders its assigned nodes in assignment order, which a manually
	// assigned slot need not share with tree order.
	const assigned: Node[] = common instanceof HTMLSlotElement ? common.assignedNodes() : []
	if (assigned.includes(branchOfA) && assigned.includes(branchOfB)) return assigned.indexOf(branchOfA) - assigned.indexOf(branchOfB)
	return branchOfA.compareDocumentPosition(branchOfB) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
}

/**
 * Hands focus on when the control holding it is removed while its area stays
 * open — a "Clear" inside `when()` whose own click falsifies the condition.
 * Without it the engine drops focus to `<body>`: the next Tab restarts at the
 * top of the page, and inside an open sheet it leaves the modal.
 *
 * Focus goes, in order, to the element `successor()` names (resolved in the
 * control's own tree scope, then each enclosing one), the tab stop Shift+Tab
 * would reach from where the control stood, the next tab stop, and finally the
 * area itself — never outside the area. An explicit successor keeps the current
 * scroll position so an in-place transition does not move the page. Automatic
 * fallback retains native scrolling to reveal the recovered focus. The area is
 * the innermost ancestor
 * carrying `data-focus-scope` (the open overlay's surface), else the page a
 * `<schmancy-area>` routed. When the area left with the control (a route
 * change, an overlay closing) nothing moves: the overlay's own restore owns it.
 */
export class FocusHandoff implements ReactiveController {
	#record: FocusRecord | undefined
	#connection: Subject<void> | undefined

	constructor(
		private readonly host: ReactiveControllerHost & HTMLElement,
		private readonly successor: () => string | null | undefined,
	) {
		host.addController(this)
	}

	hostConnected(): void {
		const connection = new Subject<void>()
		this.#connection = connection
		const control = this.host
		merge(
			// Refreshed on activation too: the removal an activation causes can arrive
			// long after focus did, and the page may have moved since.
			merge(fromEvent(control, 'focusin'), fromEvent(control, 'click')).pipe(
				filter(() => control.matches(':focus-within')),
				map((): FocusRecord | undefined => {
					const trees: Array<Document | ShadowRoot> = []
					const rungs: Rung[] = []
					let scope: HTMLElement | undefined
					let outermost: HTMLElement | undefined
					for (let at: Node | null = control; at && !scope; at = shadowIncludingParent(at)) {
						if (at instanceof ShadowRoot) trees.push(at)
						if (at !== control && at instanceof HTMLElement && at !== document.body && at !== document.documentElement) {
							outermost = at
							if (at.hasAttribute('data-focus-scope') || at.parentElement?.localName === 'schmancy-area') {
								scope = at
								break
							}
						}
						rungs.push({
							node: at,
							parent: at.parentNode,
							before: siblings(at, node => node.previousSibling),
							after: siblings(at, node => node.nextSibling),
						})
					}
					scope ??= outermost
					if (!scope) return undefined
					const scopeRoot = scope.getRootNode()
					if (scopeRoot instanceof Document || scopeRoot instanceof ShadowRoot) trees.push(scopeRoot)
					return { scope, trees, rungs: rungs.filter(rung => shadowIncludingContains(scope, rung.node)) }
				}),
			),
			// Chromium fires focusout for a removal while the control is still
			// connected; only a blur the control survives ends the record.
			fromEvent(control, 'focusout').pipe(
				observeOn(asapScheduler),
				filter(() => control.isConnected && !control.matches(':focus-within')),
				map(() => undefined),
			),
		)
			.pipe(takeUntil(connection))
			.subscribe(record => {
				this.#record = record
			})
	}

	hostDisconnected(): void {
		this.#connection?.next()
		this.#connection?.complete()
		const record = this.#record
		this.#record = undefined
		if (!record) return
		const control = this.host
		const { scope } = record
		// A macrotask, so every microtask-batched render the removal set off has
		// committed before the candidates are read.
		timer(0)
			.pipe(takeUntil(fromEvent(document, 'focusin')))
			.subscribe(() => {
				if (!scope.isConnected) return
				const active = deepActiveElement()
				if (active && active !== document.body && active !== document.documentElement) return
				// A move (a keyed list reordering it) disconnects and reconnects the same
				// control; the engine still dropped its focus, which belongs back.
				if (control.isConnected) {
					if (shadowIncludingContains(scope, control)) control.focus()
					return
				}

				const lands = (target: HTMLElement | null | undefined, options?: FocusOptions): boolean => {
					if (!target) return false
					target.focus(options)
					const now = deepActiveElement()
					return now !== null && now !== document.body && shadowIncludingContains(scope, now)
				}

				const successorId = this.successor()
				const named = successorId
					? record.trees.map(tree => tree.getElementById(successorId)).find(el => el && shadowIncludingContains(scope, el))
					: null
				if (
					named &&
					(lands(named, { preventScroll: true }) || lands(firstFocusableDescendant(named), { preventScroll: true }))
				) return

				// The removed subtree's root is the outermost rung that left while its
				// parent stayed; a surviving sibling recorded beside it marks the spot.
				const cut = record.rungs.find(rung => !rung.node.isConnected && rung.parent?.isConnected)
				// A sibling no slot renders has no place in the walk to mark.
				const anchorBefore = cut?.before.find(node => node.parentNode === cut.parent && rendered(node))
				const anchorAfter = cut?.after.find(node => node.parentNode === cut.parent && rendered(node))
				const precedesWhereItStood = (stop: HTMLElement): boolean =>
					anchorBefore
						? compareInWalkOrder(stop, anchorBefore) <= 0 || renderedChain(stop).includes(anchorBefore)
						: anchorAfter
							? compareInWalkOrder(stop, anchorAfter) < 0
							: cut?.parent
								? compareInWalkOrder(stop, cut.parent) <= 0
								: false

				const stops = focusableDescendants(scope)
				const firstAfter = stops.findIndex(stop => !precedesWhereItStood(stop))
				const before = (firstAfter === -1 ? stops : stops.slice(0, firstAfter)).reverse()
				const after = firstAfter === -1 ? [] : stops.slice(firstAfter)
				if (before.some(stop => lands(stop)) || after.some(stop => lands(stop))) return

				if (scope.hasAttribute('tabindex')) {
					lands(scope)
					return
				}
				// Focusable only while it holds focus: left on, a click anywhere inside
				// the page would focus the page itself.
				scope.setAttribute('tabindex', '-1')
				if (lands(scope)) {
					fromEvent(scope, 'blur')
						.pipe(take(1))
						.subscribe(() => scope.removeAttribute('tabindex'))
				} else {
					scope.removeAttribute('tabindex')
				}
			})
	}
}
